import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Users, BarChart3 } from 'lucide-react';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchManagerInboxRequest } from '../../../redux/actions/skillEvaluationActions';
import SkillGapTable from '../components/SkillGapTable';
import '../EvaluationDesign.css';

export default function ManagerGapPage() {
    const dispatch = useDispatch();
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { inboxManager, loading } = useSelector(state => state.skillEvaluation || { inboxManager: [], loading: false });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        dispatch(fetchCampagnesRequest());
        dispatch(fetchManagerInboxRequest());
    }, [dispatch]);

    const team = useMemo(() => {
        return (inboxManager || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            color: '#204385'
        }));
    }, [inboxManager]);

    const filteredTeam = team.filter(u => {
        const matchSearch = u.nom?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCampagne = !selectedCampagneId || u.campagneId === parseInt(selectedCampagneId);
        return matchSearch && matchCampagne;
    });

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true), 
    [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    const gapData = useMemo(() => {
        if (!selectedUser || !selectedUser.niveaux) return [];

        return selectedUser.niveaux.map(n => ({
            skill: n.skillNom,
            obtenu: n.scoreObtenu || n.scoreManager || n.scoreCollab || 0,
            requis: n.scoreRequis || 3
        }));
    }, [selectedUser]);

    return (
        <div className="eval-page">
            <div className="eval-header">
                <div>
                    <h1>Suivi des Écarts d'Équipe</h1>
                    <p>Analysez les écarts de compétences de vos collaborateurs pour identifier les besoins de montée en compétences.</p>
                </div>
            </div>

            <div className="eval-campaign-bar">
                <span className="eval-campaign-label">Filtrer par Campagne :</span>
                <select className="eval-select" value={selectedCampagneId} onChange={(e) => setSelectedCampagneId(e.target.value)}>
                    <option value="">-- Toutes les campagnes --</option>
                    {activeCampagnes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom} {c.anneeCampagne ? `(${c.anneeCampagne})` : ''}</option>
                    ))}
                </select>
            </div>

            <div className="eval-layout">
                <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search className="eval-search-icon" size={16} />
                            <input 
                                type="text" 
                                className="eval-input" 
                                placeholder="Chercher un collaborateur..." 
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)} 
                            />
                        </div>
                    </div>
                    <div className="eval-list">
                        {filteredTeam.length > 0 ? filteredTeam.map(u => (
                            <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                <div className="eval-user-info">
                                    <div className="eval-user-name">{u.nom}</div>
                                    <div className="eval-user-sub">{u.campagneNom}</div>
                                </div>
                            </div>
                        )) : (
                            <div className="eval-empty-small">Aucun collaborateur trouvé</div>
                        )}
                    </div>
                </aside>

                <main className="eval-main">
                    {!selectedUser ? (
                        <div className="eval-empty">
                            <Users size={64} className="eval-empty-icon" />
                            <h3>Analyse des Équipes</h3>
                            <p>Sélectionnez un membre de votre équipe à gauche pour visualiser ses écarts de compétences.</p>
                        </div>
                    ) : gapData.length > 0 ? (
                        <div className="eval-content">
                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <BarChart3 size={18} /> Tableau des écarts : {selectedUser.nom}
                                </div>
                                <SkillGapTable data={gapData} />
                                <div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                                    Ce tableau compare le niveau actuel maîtrisé (Obtenu) par rapport aux attentes du poste (Requis).
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Aucune donnée</h3>
                            <p>Aucune évaluation n'a été trouvée pour ce collaborateur dans cette campagne.</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
